<template>
  <div
    :ref="generateSafeId(fieldQuestionId, 'wrapper')"
    class="RMPE-DynamicField"
    :class="{ 'RMPE-HasError': fieldHasError }"
  >
    <component
      :is="layout.type"
      :image="layout.image"
      :video="layout.video"
    >
      <div
        class="field"
      >
        <div class="control">
          <label
            class="label RMPE-DynamicField-Label"
            :for="fieldQuestionId"
          >
            <span
              :class="{'RMPE-Required': question.required}"
              v-html="question.formattedLabel"
            />
          </label>

          <HintMessage :hint="question.formattedDescription" />

          <MediaMiddlePartial :layout="layout" />

          <div class="RMPE-RankingField">
            <p class="is-size-6 m-b-sm">
              Drag and drop to rank options
            </p>
            <div class="RMPE-RankingField-options isDropTarget">
              <vue-draggable
                v-model="sortedArray"
                group="ranking"
                class="list-group"
                tag="ul"
                v-bind="dragOptions"
                @end="handleDragEnd"
                @start="handleDragStart"
              >
                <transition-group
                  type="transition"
                  :name="!sortedDragState ? 'flip-list' : null"
                >
                  <li
                    v-for="(option) in sortedArray"
                    :key="option.id"
                    class="RMPE-RankingField-option list-group-item"
                  >
                    <span
                      class="RMPE-RankingField-rank select"
                    >
                      <select
                        ref="auto-focus-target"
                        v-model="option.ranking"
                        tabindex="0"
                        @change="(event) => keyboardSort(option.id, event)"
                      >
                        <option
                          :value="null"
                          disabled
                        >-</option>
                        <option
                          v-for="(item, sortedArrayIndex) in sortedArray"
                          :key="item.id"
                          :value="sortedArrayIndex + 1 "
                        >
                          {{ sortedArrayIndex + 1 }}
                        </option>
                      </select>
                    </span>
                    <span class="RMPE-RankingField-label">
                      {{ option.label }}
                    </span>
                    <font-awesome-icon
                      :icon="icon"
                      class="RMPE-RankingField-icon"
                    />
                  </li>
                </transition-group>
              </vue-draggable>
            </div>
            <p v-if="hasRankings">
              <a
                href="#"
                class="is-underlined"
                @click.prevent="resetRankings"
              >Clear all</a>
            </p>
          </div>

          <ErrorMessage
            :field-has-error="fieldHasError"
            :errors="error.errors"
            :is-blurred="isBlurred"
          />
        </div>
        <div class="RMPE-DynamicForm-SubmitContainer">
          <button
            :disabled="isNextButtonDisabled"
            class="button is-primary RMPE-CustomSubmit"
            @click="goToNextQuestion"
          >
            {{ question.goToNextQuestionButtonLabel }}
          </button>
        </div>
      </div>
    </component>
  </div>
</template>
<script>
import VueDraggable from 'vuedraggable';
import { faGripVertical } from '@fortawesome/free-solid-svg-icons';
import DynamicFieldMixin from '../../mixins/dynamic-field-mixin';
import RankingFieldMixin from '../common/ranking-field';

export default {

  components: {
    VueDraggable,
  },
  mixins: [DynamicFieldMixin, RankingFieldMixin],

  data() {
    return {
      icon: faGripVertical,
      sortedArray: [],
      sortedDragState: false,

    };
  },
  computed: {
    dragOptions() {
      return {
        animation: 200,
        group: 'description',
        disabled: false,
        ghostClass: 'ghost',
      };
    },
    hasRankings() {
      return this.sortedArray.every((item) => item.ranking !== null);
    },
  },

  mounted() {
    this.resetRankings();

    this.$nextTick(() => {
      this.autoFocusField();
    });
  },

  methods: {
    handleDragEnd() {
      this.sortedDragState = false;

      this.sortedArray.forEach((item, index) => {
        item.ranking = index + 1;
      });

      this.$nextTick(() => {
        this.$emit('change', this.sortedArray.map((item) => item.id));
      });
    },
    handleDragStart() {
      this.sortedDragState = true;
    },
    resetRankings() {
      this.sortedArray = this.question.options.map((option) => {
        return {
          id: option,
          label: option,
          ranking: null,
        };
      });
    },
    keyboardSort(option, event) {
      // console.log(this.sortedArray, option, event);

      // IF an item already exists with the rank just updated, reset it to null to ensure
      // it doesnt have a duplicated ranking
      const existingRankedItemToBeReplaced = this.sortedArray.find((item) => {
        return item.ranking === +event.target.value
          && item.id !== option;
      });

      if (existingRankedItemToBeReplaced !== undefined) {
        existingRankedItemToBeReplaced.ranking = null;
      }

      // Find all possible rankings so we can use this to fill gaps.
      let possibleRanks = this.sortedArray.map((item, index) => {
        return index + 1;
      });

      // Get unranked items
      const unrankedItems = this.sortedArray.filter(item => item.ranking === null);

      // Get ranked items and sort them
      let sortedItems = this.sortedArray.filter(item => item.ranking !== null);

      sortedItems = sortedItems.sort((a, b) => {
        return a.ranking - b.ranking;
      });

      const currentRanks = sortedItems.map((item) => item.ranking);

      // Trim down possible ranks to just those that havent been used.
      possibleRanks = possibleRanks.filter((rank) => {
        return !currentRanks.includes(rank);
      });

      let final = [...sortedItems, ...unrankedItems];

      // For any unranked items, use a possible rank
      final.forEach((item) => {
        if (item.ranking === null) {
          item.ranking = possibleRanks[0]; //eslint-disable-line
          possibleRanks.shift();
        }
      });

      final = final.sort((a, b) => {
        return a.ranking - b.ranking;
      });

      this.sortedArray = final;

      this.$nextTick(() => {
        this.$emit('change', this.sortedArray.map((item) => item.id));
      });
    },
  },
};
</script>

<style scoped lang="scss">
.RMPE-RankingField {

}
.RMPE-RankingField-options {
  display: flex;
  flex-direction: column;
}
.RMPE-RankingField-option {
  background-color: #000;
  color: #FFF;
  padding: 10px 20px;
  margin-bottom: 8px;
  border: 1px solid #FFF;
  border-radius: 5px;
  display: flex;
  align-items: center;

  &.isNotDraggable {
    opacity: 0.3;
  }
}
.RMPE-RankingField-rank {
  margin-right: 20px;
}
.RMPE-RankingField-icon {
  color: #FFF;
  margin-left: auto;
}
.RMPE-RankingField-rank {
  select {
    border-radius: 5px;
    color: #000;
  }
}

.flip-list-move {
  transition: transform 0.5s;
}
.no-move {
  transition: transform 0s;
}
.ghost {
  opacity: 0;
}
.list-group {
  min-height: 20px;
}
.list-group-item {
  cursor: move;
}
.list-group-item i {
  cursor: pointer;
}

</style>
